import { useState } from 'react';
import { CalendarRange, Check, Loader2, MapPin, Phone, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { ReasonModal } from '@/components/booking-ops-modals';
import { form_error_message } from '@/lib/forms';
import { use_vendor_training_packages, use_accept_training_package, use_reject_training_package } from '@/features/training-package/hooks';
import type { TrainingPackage, TrainingPackageStatus } from '@/features/training-package/types';
import type { BookingStatus } from '@/features/bookings/types';
import { slot_label } from '@/lib/datetime';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PACKAGE_STATUS_STYLE: Record<TrainingPackageStatus, string> = {
  PENDING_PAYMENT: 'bg-muted text-muted-foreground',
  ACTIVE: 'bg-success/12 text-success',
  COMPLETED: 'bg-info/12 text-info',
  CANCELLED: 'bg-destructive/12 text-destructive',
};

const SESSION_STATUS_STYLE: Record<BookingStatus, string> = {
  PENDING_PAYMENT: 'bg-muted text-muted-foreground',
  AWAITING_VENDOR: 'bg-warning/15 text-warning',
  CONFIRMED: 'bg-info/12 text-info',
  IN_PROGRESS: 'bg-primary/12 text-primary',
  COMPLETED: 'bg-success/12 text-success',
  CANCELLED: 'bg-destructive/12 text-destructive',
  NO_SHOW: 'bg-destructive/12 text-destructive',
};

function format_session(iso: string): string {
  return slot_label(iso);
}

/** View grouping each package's child sessions under the recurring course, with a
 *  one-time "Accept package" / "Reject package" pair that confirms or declines every
 *  awaiting session at once. */
export function VendorTrainingPackagesPage() {
  const packages_q = use_vendor_training_packages();
  const accept_m = use_accept_training_package();
  const reject_m = use_reject_training_package();
  // Keyed by package so a failure surfaces on the card the vendor acted on, not on all of them.
  const [row_error, set_row_error] = useState<{ id: string; message: string } | null>(null);
  const [rejecting, set_rejecting] = useState<TrainingPackage | null>(null);
  const packages = packages_q.data ?? [];

  async function accept(id: string) {
    set_row_error(null);
    try {
      await accept_m.mutateAsync(id);
    } catch (err) {
      set_row_error({ id, message: form_error_message(err, 'Could not accept this package.') });
    }
  }

  async function reject(id: string, reason: string) {
    set_row_error(null);
    try {
      await reject_m.mutateAsync({ id, reason: reason || undefined });
      set_rejecting(null);
    } catch (err) {
      set_rejecting(null);
      set_row_error({ id, message: form_error_message(err, 'Could not reject this package.') });
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Packages"
        description="Multi-session training courses and the individual sessions they generate."
      />

      {packages_q.isLoading ? (
        <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : packages_q.error ? (
        <div className="p-6 text-sm text-destructive" data-testid="training-packages-error">
          {form_error_message(packages_q.error, 'Failed to load packages.')}
        </div>
      ) : packages.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="training-packages-empty">
            <CalendarRange className="h-6 w-6" />
            No training packages yet.
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-4" data-testid="training-packages-list">
          {packages.map((p) => {
            const completed = p.sessions.filter((s) => s.status === 'COMPLETED').length;
            const awaiting = p.sessions.filter((s) => s.status === 'AWAITING_VENDOR').length;
            const can_act = p.status === 'ACTIVE' && awaiting > 0;
            return (
              <li key={p.id} data-testid={`training-package-${p.id}`}>
                <Card>
                  <CardContent className="flex flex-col gap-3 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">
                            {p.days_of_week.map((d) => DAY_LABELS[d]).join(', ')} · {p.time}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PACKAGE_STATUS_STYLE[p.status]}`}>
                            {p.status.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {p.weeks} week(s) from {p.start_date} · {completed}/{p.total_sessions} sessions done
                        </p>
                        {p.pickup_address && (
                          <p className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3 shrink-0" /> {p.pickup_address}
                          </p>
                        )}
                        {p.contact_phone && (
                          <p className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3 shrink-0" /> {p.contact_phone}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        <div className="font-semibold">₹{p.total_inr}</div>
                        {p.payment && (
                          <div className="text-xs text-muted-foreground">
                            {p.payment.status === 'CAPTURED' ? 'Paid' : p.payment.status.toLowerCase()}
                          </div>
                        )}
                      </div>
                      {can_act && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => accept(p.id)}
                            disabled={accept_m.isPending || reject_m.isPending}
                            data-testid={`training-package-accept-${p.id}`}
                            title={`Accept all ${awaiting} pending session${awaiting > 1 ? 's' : ''} in this package`}
                          >
                            {accept_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Accept package ({awaiting})
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => set_rejecting(p)}
                            disabled={accept_m.isPending || reject_m.isPending}
                            data-testid={`training-package-reject-${p.id}`}
                            title={`Decline all ${awaiting} pending session${awaiting > 1 ? 's' : ''} in this package`}
                          >
                            {reject_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                            Reject package ({awaiting})
                          </Button>
                        </div>
                      )}
                    </div>
                    {row_error?.id === p.id && (
                      <p className="text-xs text-destructive" data-testid={`training-package-accept-error-${p.id}`}>
                        {row_error.message}
                      </p>
                    )}

                    <ul className="divide-y divide-border rounded-md border border-border" data-testid={`training-package-sessions-${p.id}`}>
                      {p.sessions.map((s) => (
                        <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span>{format_session(s.slot_start)}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${SESSION_STATUS_STYLE[s.status]}`}>
                            {s.status.replace(/_/g, ' ').toLowerCase()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {rejecting && (
        <ReasonModal
          title={`Reject ${rejecting.sessions.filter((s) => s.status === 'AWAITING_VENDOR').length} pending session(s)?`}
          label="Why are you declining this package?"
          pending={reject_m.isPending}
          on_submit={(reason) => reject(rejecting.id, reason)}
          on_close={() => set_rejecting(null)}
        />
      )}
    </div>
  );
}
